export type ReviewRecommendation = "Auto-Renew" | "Conditional Renewal" | "Re-Advertise";

export interface RatingCategory {
  name: string;
  score: number; // 1-5
}

export interface SupplierReview {
  id: string;
  supplier: string;
  quarter: string; // e.g. "Q1 2025"
  year: number;
  quarterNumber: number; // 1-4
  categories: RatingCategory[];
  overallScore: number; // average of categories, rounded to 1 decimal
  recommendation: ReviewRecommendation;
  reviewer: string;
  comments: string;
  reviewDate: string;
}

export const ratingCategories = [
  "Quality of Service",
  "Timeliness & Delivery",
  "Communication",
  "Value for Money",
  "Compliance",
];

export const recommendationColors: Record<ReviewRecommendation, { bg: string; text: string; dot: string }> = {
  "Auto-Renew": { bg: "bg-accent-50", text: "text-accent-700", dot: "bg-accent-500" },
  "Conditional Renewal": { bg: "bg-accent-100", text: "text-accent-800", dot: "bg-accent-600" },
  "Re-Advertise": { bg: "bg-accent-200", text: "text-accent-900", dot: "bg-accent-700" },
};

export const mockSupplierReviews: SupplierReview[] = [
  // --- Dimension Data ---
  {
    id: "rev-001",
    supplier: "Dimension Data (Pty) Ltd",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 3 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.6,
    recommendation: "Conditional Renewal",
    reviewer: "Thabo Molefe",
    comments: "Technical delivery remains strong but communication on project milestones has been inconsistent this quarter. Pricing is becoming less competitive compared to market alternatives.",
    reviewDate: "2026-04-05",
  },
  {
    id: "rev-002",
    supplier: "Dimension Data (Pty) Ltd",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 5 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 5 },
    ],
    overallScore: 4.4,
    recommendation: "Auto-Renew",
    reviewer: "Thabo Molefe",
    comments: "Excellent performance across all metrics. All SLA targets met. No compliance issues noted during the quarter.",
    reviewDate: "2025-12-18",
  },
  {
    id: "rev-003",
    supplier: "Dimension Data (Pty) Ltd",
    quarter: "Q3 2025",
    year: 2025,
    quarterNumber: 3,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.8,
    recommendation: "Conditional Renewal",
    reviewer: "Thabo Molefe",
    comments: "Solid quarter overall. Cost overruns on the network upgrade project need attention before the next review cycle.",
    reviewDate: "2025-10-02",
  },

  // --- Servest Facility Management ---
  {
    id: "rev-004",
    supplier: "Servest Facility Management",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 2 },
      { name: "Timeliness & Delivery", score: 2 },
      { name: "Communication", score: 1 },
      { name: "Value for Money", score: 2 },
      { name: "Compliance", score: 2 },
    ],
    overallScore: 1.8,
    recommendation: "Re-Advertise",
    reviewer: "Nomsa Dlamini",
    comments: "Service levels have deteriorated significantly. Multiple unresolved maintenance tickets spanning over 60 days. Unresponsive account management. Strongly recommend re-advertising this contract.",
    reviewDate: "2026-03-28",
  },
  {
    id: "rev-005",
    supplier: "Servest Facility Management",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 2 },
      { name: "Communication", score: 2 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 3 },
    ],
    overallScore: 2.6,
    recommendation: "Conditional Renewal",
    reviewer: "Nomsa Dlamini",
    comments: "Service quality declining. Escalated to senior management — improvement plan promised but not yet delivered.",
    reviewDate: "2025-12-10",
  },

  // --- Deloitte Consulting ---
  {
    id: "rev-006",
    supplier: "Deloitte Consulting",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 5 },
      { name: "Timeliness & Delivery", score: 5 },
      { name: "Communication", score: 5 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 5 },
    ],
    overallScore: 4.8,
    recommendation: "Auto-Renew",
    reviewer: "Sipho Khumalo",
    comments: "Outstanding performance. Delivered the fiscal policy review ahead of schedule with exceptional quality. Highly professional engagement throughout.",
    reviewDate: "2026-03-15",
  },
  {
    id: "rev-007",
    supplier: "Deloitte Consulting",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 5 },
    ],
    overallScore: 4.0,
    recommendation: "Auto-Renew",
    reviewer: "Sipho Khumalo",
    comments: "Strong delivery across the board. Premium pricing is the only concern — recommend benchmarking against market rates before renewal.",
    reviewDate: "2025-12-05",
  },

  // --- Bytes Technology Group ---
  {
    id: "rev-008",
    supplier: "Bytes Technology Group",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 3 },
      { name: "Communication", score: 2 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.0,
    recommendation: "Conditional Renewal",
    reviewer: "Lindiwe Nkosi",
    comments: "Adequate but unremarkable. Licensing renewals handled correctly but response times to support queries need improvement.",
    reviewDate: "2026-04-10",
  },

  // --- WBHO Construction ---
  {
    id: "rev-009",
    supplier: "WBHO Construction",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 3 },
      { name: "Communication", score: 3 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.4,
    recommendation: "Conditional Renewal",
    reviewer: "Mandla Zulu",
    comments: "Road infrastructure project progressing well. Some delays due to weather but mitigation plans in place. Cost management within budget.",
    reviewDate: "2026-04-02",
  },
  {
    id: "rev-010",
    supplier: "WBHO Construction",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 2 },
      { name: "Communication", score: 2 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 3 },
    ],
    overallScore: 2.6,
    recommendation: "Conditional Renewal",
    reviewer: "Mandla Zulu",
    comments: "Project fell behind schedule by 6 weeks. Subcontractor management needs significant improvement. Quality of work remains acceptable.",
    reviewDate: "2025-12-20",
  },

  // --- Gijima Holdings ---
  {
    id: "rev-011",
    supplier: "Gijima Holdings",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 4.0,
    recommendation: "Auto-Renew",
    reviewer: "Dr. Palesa Mokoena",
    comments: "Digital health platform performing well. Patient record system uptime at 99.7%. Support team responsive and knowledgeable.",
    reviewDate: "2026-03-20",
  },
  {
    id: "rev-012",
    supplier: "Gijima Holdings",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 3 },
      { name: "Communication", score: 3 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 3 },
    ],
    overallScore: 3.0,
    recommendation: "Conditional Renewal",
    reviewer: "Dr. Palesa Mokoena",
    comments: "System migration caused temporary disruptions. Rollback plan executed well. Performance returning to expected levels.",
    reviewDate: "2025-12-15",
  },

  // --- EOH Mthombo ---
  {
    id: "rev-013",
    supplier: "EOH Mthombo",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 3 },
      { name: "Communication", score: 2 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 2 },
    ],
    overallScore: 2.6,
    recommendation: "Conditional Renewal",
    reviewer: "Busisiwe Mthembu",
    comments: "Contract nearing end of term. Performance has been inconsistent — education technology rollout delayed in rural districts. Compliance documentation incomplete.",
    reviewDate: "2025-12-08",
  },

  // --- Mediclinic Southern Africa ---
  {
    id: "rev-014",
    supplier: "Mediclinic Southern Africa",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 5 },
      { name: "Timeliness & Delivery", score: 5 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 5 },
    ],
    overallScore: 4.6,
    recommendation: "Auto-Renew",
    reviewer: "Jabulani Sibeko",
    comments: "Exceptional healthcare delivery to correctional facilities. All clinical governance standards met. Patient outcomes well above contracted targets.",
    reviewDate: "2026-04-08",
  },
  {
    id: "rev-015",
    supplier: "Mediclinic Southern Africa",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.8,
    recommendation: "Conditional Renewal",
    reviewer: "Jabulani Sibeko",
    comments: "Consistent quality. Pharmaceutical supply chain improved this quarter. Cost per patient-day trending above inflation — needs monitoring.",
    reviewDate: "2025-12-12",
  },

  // --- PwC South Africa ---
  {
    id: "rev-016",
    supplier: "PwC South Africa",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 5 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 5 },
    ],
    overallScore: 4.4,
    recommendation: "Auto-Renew",
    reviewer: "Nomvula Dube",
    comments: "Audit work of the highest standard. All findings well-documented and presented professionally. Beneficial knowledge transfer to internal audit team.",
    reviewDate: "2026-03-25",
  },
  {
    id: "rev-017",
    supplier: "PwC South Africa",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.8,
    recommendation: "Conditional Renewal",
    reviewer: "Nomvula Dube",
    comments: "Professional and thorough. Interim audit completed on schedule. Fee proposal for the upcoming financial year is 8% higher — requires justification.",
    reviewDate: "2025-12-22",
  },

  // --- BCX ---
  {
    id: "rev-018",
    supplier: "BCX",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 5 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 4.2,
    recommendation: "Auto-Renew",
    reviewer: "Thandiwe Mahlangu",
    comments: "Cloud migration completed two weeks ahead of schedule. Zero downtime during cutover. Excellent project management throughout.",
    reviewDate: "2026-04-12",
  },
  {
    id: "rev-019",
    supplier: "BCX",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 3 },
      { name: "Communication", score: 3 },
      { name: "Value for Money", score: 4 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.4,
    recommendation: "Conditional Renewal",
    reviewer: "Thandiwe Mahlangu",
    comments: "Planning phase for cloud migration ran slightly long but thorough. Architecture review identified cost optimisation opportunities.",
    reviewDate: "2025-12-05",
  },

  // --- SITA ---
  {
    id: "rev-020",
    supplier: "SITA (State Information Technology Agency)",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 2 },
      { name: "Communication", score: 2 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 2.8,
    recommendation: "Conditional Renewal",
    reviewer: "Col. Hendrik van der Merwe",
    comments: "Security clearance renewal process causing delays. Core cybersecurity operations maintained but project delivery lagging. Bureaucratic bottlenecks need addressing.",
    reviewDate: "2026-03-30",
  },
  {
    id: "rev-021",
    supplier: "SITA (State Information Technology Agency)",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 2 },
      { name: "Communication", score: 2 },
      { name: "Value for Money", score: 2 },
      { name: "Compliance", score: 3 },
    ],
    overallScore: 2.4,
    recommendation: "Re-Advertise",
    reviewer: "Col. Hendrik van der Merwe",
    comments: "Persistent underperformance. Threat detection response times exceed contracted SLAs. Despite being a state entity, market alternatives may offer better value.",
    reviewDate: "2025-12-01",
  },

  // --- KPMG Services ---
  {
    id: "rev-022",
    supplier: "KPMG Services",
    quarter: "Q1 2026",
    year: 2026,
    quarterNumber: 1,
    categories: [
      { name: "Quality of Service", score: 4 },
      { name: "Timeliness & Delivery", score: 4 },
      { name: "Communication", score: 4 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 5 },
    ],
    overallScore: 4.0,
    recommendation: "Auto-Renew",
    reviewer: "Rethabile Mofokeng",
    comments: "Water infrastructure PMO running efficiently. All statutory reporting submitted on time. Good stakeholder management across multiple municipalities.",
    reviewDate: "2026-04-01",
  },
  {
    id: "rev-023",
    supplier: "KPMG Services",
    quarter: "Q4 2025",
    year: 2025,
    quarterNumber: 4,
    categories: [
      { name: "Quality of Service", score: 3 },
      { name: "Timeliness & Delivery", score: 3 },
      { name: "Communication", score: 3 },
      { name: "Value for Money", score: 3 },
      { name: "Compliance", score: 4 },
    ],
    overallScore: 3.2,
    recommendation: "Conditional Renewal",
    reviewer: "Rethabile Mofokeng",
    comments: "Adequate project oversight. Reporting templates could be more detailed. Recommend including milestone-based KPIs in the next contract term.",
    reviewDate: "2025-12-14",
  },
];

export function getLatestReviewForSupplier(supplier: string): SupplierReview | undefined {
  return mockSupplierReviews
    .filter((r) => r.supplier === supplier)
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.quarterNumber - a.quarterNumber;
    })[0];
}

export function getAverageRatingForSupplier(supplier: string): number {
  const reviews = mockSupplierReviews.filter((r) => r.supplier === supplier);
  if (reviews.length === 0) return 0;
  const sum = reviews.reduce((acc, r) => acc + r.overallScore, 0);
  return Math.round((sum / reviews.length) * 10) / 10;
}

export function getSupplierRatingTrend(supplier: string): { quarter: string; score: number }[] {
  return mockSupplierReviews
    .filter((r) => r.supplier === supplier)
    .sort((a, b) => a.year - b.year || a.quarterNumber - b.quarterNumber)
    .map((r) => ({ quarter: r.quarter, score: r.overallScore }));
}

export function getAllSuppliersWithRatings(): { supplier: string; latestReview: SupplierReview; averageRating: number }[] {
  const suppliers = [...new Set(mockSupplierReviews.map((r) => r.supplier))];
  return suppliers.map((supplier) => ({
    supplier,
    latestReview: getLatestReviewForSupplier(supplier)!,
    averageRating: getAverageRatingForSupplier(supplier),
  }));
}